import React, { useState, useEffect, useCallback } from 'react';
import { 
  Sparkles, 
  Check, 
  X, 
  RefreshCw, 
  MessageSquare, 
  Eye,
  ChevronDown,
  ChevronUp,
  Tag,
  FileText,
  Heading,
  Clock,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RotateCcw,
  Send,
  Filter,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminAIReview = () => {
  const [spots, setSpots] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [revisionFocus, setRevisionFocus] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [processingSpot, setProcessingSpot] = useState(null);
  const [expandedSpots, setExpandedSpots] = useState({});

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const fetchSpots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai/pending-review?status=${statusFilter}&limit=50`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSpots(data.spots || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching spots:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getAuthHeaders]);

  useEffect(() => {
    fetchSpots();
  }, [fetchSpots]);

  const handleApprove = async (spotId, applyOptions = { title: true, description: true, tags: true }) => {
    setProcessingSpot(spotId);
    try {
      const response = await fetch(`${API_URL}/api/ai/approve/${spotId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          spot_id: spotId,
          apply_title: applyOptions.title,
          apply_description: applyOptions.description,
          apply_tags: applyOptions.tags
        })
      });
      
      if (response.ok) {
        fetchSpots();
      }
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setProcessingSpot(null);
    }
  };

  const handleReject = async (spotId) => {
    setProcessingSpot(spotId);
    try {
      const response = await fetch(`${API_URL}/api/ai/reject/${spotId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: 'Rejeté par admin' })
      });
      
      if (response.ok) {
        fetchSpots();
      }
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setProcessingSpot(null);
    }
  };

  const handleRequestRevision = async () => {
    if (!selectedSpot || !revisionFeedback) return;
    
    setProcessingSpot(selectedSpot.id);
    try {
      const response = await fetch(`${API_URL}/api/ai/request-revision/${selectedSpot.id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          spot_id: selectedSpot.id,
          feedback: revisionFeedback,
          focus_on: revisionFocus
        })
      });
      
      if (response.ok) {
        setShowRevisionModal(false);
        setRevisionFeedback('');
        setRevisionFocus([]);
        setSelectedSpot(null);
        fetchSpots();
      }
    } catch (error) {
      console.error('Error requesting revision:', error);
    } finally {
      setProcessingSpot(null);
    }
  };

  const toggleExpand = (spotId) => {
    setExpandedSpots(prev => ({
      ...prev,
      [spotId]: !prev[spotId]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { color: 'bg-yellow-500', icon: Clock, text: 'En attente' },
      approved: { color: 'bg-green-500', icon: CheckCircle, text: 'Approuvé' },
      rejected: { color: 'bg-red-500', icon: XCircle, text: 'Rejeté' },
      revision_requested: { color: 'bg-blue-500', icon: RotateCcw, text: 'Révision demandée' }
    };
    const badge = badges[status] || badges.pending_review;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-white ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Modifications IA</h1>
            <p className="text-gray-600">Validez les suggestions de l'intelligence artificielle</p>
          </div>
        </div>
        
        <button
          onClick={fetchSpots}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div 
          className={`p-4 rounded-lg cursor-pointer transition-all ${statusFilter === 'pending_review' ? 'bg-yellow-500 ring-2 ring-yellow-400 text-white' : 'bg-white hover:bg-gray-50 border border-gray-200'}`}
          onClick={() => setStatusFilter('pending_review')}
        >
          <div className={`flex items-center gap-2 mb-1 ${statusFilter === 'pending_review' ? 'text-yellow-100' : 'text-yellow-600'}`}>
            <Clock className="w-5 h-5" />
            <span className="text-sm">En attente</span>
          </div>
          <div className={`text-2xl font-bold ${statusFilter === 'pending_review' ? 'text-white' : 'text-gray-900'}`}>{stats.pending_review || 0}</div>
        </div>
        
        <div 
          className={`p-4 rounded-lg cursor-pointer transition-all ${statusFilter === 'approved' ? 'bg-green-600 ring-2 ring-green-400 text-white' : 'bg-white hover:bg-gray-50 border border-gray-200'}`}
          onClick={() => setStatusFilter('approved')}
        >
          <div className={`flex items-center gap-2 mb-1 ${statusFilter === 'approved' ? 'text-green-100' : 'text-green-600'}`}>
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">Approuvés</span>
          </div>
          <div className={`text-2xl font-bold ${statusFilter === 'approved' ? 'text-white' : 'text-gray-900'}`}>{stats.approved || 0}</div>
        </div>
        
        <div 
          className={`p-4 rounded-lg cursor-pointer transition-all ${statusFilter === 'rejected' ? 'bg-red-600 ring-2 ring-red-400 text-white' : 'bg-white hover:bg-gray-50 border border-gray-200'}`}
          onClick={() => setStatusFilter('rejected')}
        >
          <div className={`flex items-center gap-2 mb-1 ${statusFilter === 'rejected' ? 'text-red-100' : 'text-red-600'}`}>
            <XCircle className="w-5 h-5" />
            <span className="text-sm">Rejetés</span>
          </div>
          <div className={`text-2xl font-bold ${statusFilter === 'rejected' ? 'text-white' : 'text-gray-900'}`}>{stats.rejected || 0}</div>
        </div>
        
        <div 
          className={`p-4 rounded-lg cursor-pointer transition-all ${statusFilter === 'revision_requested' ? 'bg-blue-600 ring-2 ring-blue-400 text-white' : 'bg-white hover:bg-gray-50 border border-gray-200'}`}
          onClick={() => setStatusFilter('revision_requested')}
        >
          <div className={`flex items-center gap-2 mb-1 ${statusFilter === 'revision_requested' ? 'text-blue-100' : 'text-blue-600'}`}>
            <RotateCcw className="w-5 h-5" />
            <span className="text-sm">En révision</span>
          </div>
          <div className={`text-2xl font-bold ${statusFilter === 'revision_requested' ? 'text-white' : 'text-gray-900'}`}>{stats.revision_requested || 0}</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['pending_review', 'approved', 'rejected', 'revision_requested', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              statusFilter === status
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {status === 'pending_review' && 'En attente'}
            {status === 'approved' && 'Approuvés'}
            {status === 'rejected' && 'Rejetés'}
            {status === 'revision_requested' && 'En révision'}
            {status === 'all' && 'Tous'}
          </button>
        ))}
      </div>

      {/* Spots List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : spots.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucun spot à afficher pour ce filtre</p>
        </div>
      ) : (
        <div className="space-y-4">
          {spots.map((spot) => (
            <div
              key={spot.id}
              className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm"
            >
              {/* Spot Header */}
              <div 
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-750"
                onClick={() => toggleExpand(spot.id)}
              >
                {/* Cover Image */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                  {spot.cover_photo_url ? (
                    <img
                      src={spot.cover_photo_url}
                      alt={spot.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(spot.ai_status)}
                    <span className="text-gray-500 text-xs">
                      {formatDate(spot.ai_processed_at)}
                    </span>
                  </div>
                  <h3 className="text-white font-medium truncate">{spot.title}</h3>
                  <p className="text-sm text-gray-400 truncate">
                    {spot.ai_optimized_title && spot.ai_optimized_title !== spot.title ? (
                      <span className="text-purple-400">→ {spot.ai_optimized_title}</span>
                    ) : (
                      <span className="text-gray-500">Pas de modification de titre</span>
                    )}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://platform.sun-spot.app/spot/${spot.id}?details=true`, '_blank');
                    }}
                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    title="Voir le rendu sur la plateforme"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  {spot.ai_status === 'pending_review' && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(spot.id);
                        }}
                        disabled={processingSpot === spot.id}
                        className="p-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50"
                        title="Approuver"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(spot.id);
                        }}
                        disabled={processingSpot === spot.id}
                        className="p-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
                        title="Rejeter"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSpot(spot);
                          setShowRevisionModal(true);
                        }}
                        disabled={processingSpot === spot.id}
                        className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50"
                        title="Demander révision"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {expandedSpots[spot.id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedSpots[spot.id] && (
                <div className="border-t border-gray-700 p-4 space-y-4">
                  {/* Title Comparison */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-900 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                        <Heading className="w-4 h-4" />
                        Titre original
                      </div>
                      <p className="text-white">{spot.title}</p>
                    </div>
                    <div className="bg-purple-900/30 p-3 rounded-lg border border-purple-500/30">
                      <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
                        <Sparkles className="w-4 h-4" />
                        Titre suggéré par IA
                      </div>
                      <p className="text-white">{spot.ai_optimized_title || 'Aucune suggestion'}</p>
                    </div>
                  </div>
                  
                  {/* Description Comparison */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-900 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                        <FileText className="w-4 h-4" />
                        Description originale
                      </div>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {spot.description || 'Aucune description'}
                      </p>
                    </div>
                    <div className="bg-purple-900/30 p-3 rounded-lg border border-purple-500/30">
                      <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
                        <Sparkles className="w-4 h-4" />
                        Description suggérée par IA
                      </div>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {spot.ai_optimized_description || 'Aucune suggestion'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Tags Comparison */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-900 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                        <Tag className="w-4 h-4" />
                        Tags originaux
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(spot.tags || []).map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                        {(!spot.tags || spot.tags.length === 0) && (
                          <span className="text-gray-500 text-sm">Aucun tag</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-purple-900/30 p-3 rounded-lg border border-purple-500/30">
                      <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
                        <Sparkles className="w-4 h-4" />
                        Tags suggérés par IA
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(spot.ai_suggested_tags || []).map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-purple-700/50 text-purple-200 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                        {(!spot.ai_suggested_tags || spot.ai_suggested_tags.length === 0) && (
                          <span className="text-gray-500 text-sm">Aucune suggestion</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Geo Validation */}
                  {spot.geo_validation && (
                    <div className={`p-3 rounded-lg ${
                      spot.geo_validation.verdict === 'VALIDE' ? 'bg-green-900/30 border border-green-500/30' :
                      spot.geo_validation.verdict === 'SUSPECT' ? 'bg-red-900/30 border border-red-500/30' :
                      'bg-yellow-900/30 border border-yellow-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`w-4 h-4 ${
                          spot.geo_validation.verdict === 'VALIDE' ? 'text-green-400' :
                          spot.geo_validation.verdict === 'SUSPECT' ? 'text-red-400' :
                          'text-yellow-400'
                        }`} />
                        <span className="text-sm font-medium text-white">
                          Validation géographique: {spot.geo_validation.verdict}
                        </span>
                        <span className="text-xs text-gray-400">
                          (Score: {spot.geo_validation.score}/100)
                        </span>
                      </div>
                      {spot.geo_validation.reasons && spot.geo_validation.reasons.length > 0 && (
                        <ul className="text-sm text-gray-300 space-y-1">
                          {spot.geo_validation.reasons.map((reason, i) => (
                            <li key={i}>• {reason}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  
                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {spot.creator_username || 'Inconnu'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Créé le {formatDate(spot.created_at)}
                    </span>
                    {spot.ai_revision_count > 0 && (
                      <span className="flex items-center gap-1 text-blue-400">
                        <RotateCcw className="w-4 h-4" />
                        {spot.ai_revision_count} révision(s)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Revision Modal */}
      {showRevisionModal && selectedSpot && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-lg">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-medium text-white">Demander une révision</h3>
              <p className="text-sm text-gray-400">{selectedSpot.title}</p>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Focus de la révision (optionnel)
                </label>
                <div className="flex flex-wrap gap-2">
                  {['title', 'description', 'tags'].map((focus) => (
                    <button
                      key={focus}
                      onClick={() => {
                        setRevisionFocus(prev => 
                          prev.includes(focus) 
                            ? prev.filter(f => f !== focus)
                            : [...prev, focus]
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        revisionFocus.includes(focus)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {focus === 'title' && 'Titre'}
                      {focus === 'description' && 'Description'}
                      {focus === 'tags' && 'Tags'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Feedback pour l'IA *
                </label>
                <textarea
                  value={revisionFeedback}
                  onChange={(e) => setRevisionFeedback(e.target.value)}
                  placeholder="Ex: Le titre est trop long, la description manque de détails sur l'accessibilité..."
                  className="w-full h-32 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRevisionModal(false);
                  setRevisionFeedback('');
                  setRevisionFocus([]);
                  setSelectedSpot(null);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRequestRevision}
                disabled={!revisionFeedback || processingSpot}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                Envoyer à l'IA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAIReview;
